import { useCallback, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  createOverlayEntryState,
  joinPath,
  useRoutedDialog,
  useRoutedDrawer,
  useRouteNotFoundToast,
} from "../../../hooks/routedOverlays";

export const BUNDLE_DRAWER_SEGMENT = "bundle";
export const BUNDLE_CREATE_PATH = "create";
export const BUNDLE_CATALOG_SUBSET_PARAM = "catalogSubsetId";

const parsePositiveInt = (value: string | null | undefined): number | null => {
  if (!value || !/^\d+$/.test(value)) {
    return null;
  }
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

type OpenBundle = Readonly<{
  bundleId: number;
  catalogSubsetId: number | null;
  drawerPath: string;
}>;

/**
 * Routes the bundles page overlays below `bundlesBase`:
 * `bundle/{bundleId}/{tab}?catalogSubsetId=` for the edit drawer and `create/{step}` for the
 * create wizard. The catalog subset travels as a search param because the bundle cannot be
 * loaded or patched without it in Customer Success.
 */
export function useRoutedBundleOverlays(bundlesBase: string) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const drawer = useRoutedDrawer({
    segment: BUNDLE_DRAWER_SEGMENT,
    basePath: bundlesBase,
  });
  const [drawerDirty, setDrawerDirty] = useState(false);
  // Guards Back out of a dirty drawer; tab and nested overlay routes stay below the pattern.
  const drawerGuard = useRoutedDialog({
    path: `${BUNDLE_DRAWER_SEGMENT}/:bundleId`,
    basePath: bundlesBase,
    end: false,
    isDirty: drawerDirty,
  });
  // The drawer's save and discard callbacks can outlive the render (and tab key) they came from.
  const drawerGuardCloseRef = useRef(drawerGuard.close);
  drawerGuardCloseRef.current = drawerGuard.close;
  const closeDrawer = useCallback(() => {
    drawerGuardCloseRef.current();
  }, []);

  const bundleId = drawer.isOpen ? parsePositiveInt(drawer.id) : null;
  const catalogSubsetId = parsePositiveInt(
    searchParams.get(BUNDLE_CATALOG_SUBSET_PARAM),
  );
  useRouteNotFoundToast(
    drawer.isOpen && bundleId == null ? (drawer.id ?? "") : null,
    "That bundle could not be found.",
    closeDrawer,
  );

  // Keeps the drawer mounted with its last bundle while the close transition runs.
  const lastBundleRef = useRef<OpenBundle | null>(null);
  if (drawer.isOpen && bundleId != null && drawer.drawerPath) {
    lastBundleRef.current = {
      bundleId,
      catalogSubsetId,
      drawerPath: drawer.drawerPath,
    };
  }

  const openBundle = useCallback(
    (nextBundleId: number, nextCatalogSubsetId: number | null) => {
      const search =
        nextCatalogSubsetId == null
          ? ""
          : `?${new URLSearchParams({
              [BUNDLE_CATALOG_SUBSET_PARAM]: String(nextCatalogSubsetId),
            }).toString()}`;
      navigate(
        {
          pathname: joinPath(
            bundlesBase,
            BUNDLE_DRAWER_SEGMENT,
            String(nextBundleId),
          ),
          search,
        },
        { state: createOverlayEntryState(location) },
      );
    },
    [bundlesBase, navigate, location],
  );

  const [wizardDirty, setWizardDirty] = useState(false);
  const wizard = useRoutedDialog({
    path: BUNDLE_CREATE_PATH,
    basePath: bundlesBase,
    end: false,
    isDirty: wizardDirty,
  });

  return {
    drawer: {
      isOpen: drawer.isOpen
        ? bundleId != null
        : drawerGuard.isOpen && lastBundleRef.current != null,
      bundle: lastBundleRef.current,
      open: openBundle,
      close: closeDrawer,
      onDirtyChange: setDrawerDirty,
      discardPrompt: drawerGuard.discardPrompt,
    },
    wizard: {
      isOpen: wizard.isOpen,
      open: wizard.open,
      close: wizard.close,
      routeBase: joinPath(bundlesBase, BUNDLE_CREATE_PATH),
      onDirtyChange: setWizardDirty,
      discardPrompt: wizard.discardPrompt,
    },
  };
}
